import React, { useState, useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { Checkbox, Dimmer, Input, Loader } from 'semantic-ui-react'
import ClimaCheckQuery from '~/QueryHooks/ClimaChQuery'

interface MemoryField {
  key: string
  label: string
}

interface MemoryFieldsData {
  memoryFields: Record<string, MemoryField[]>
}

const MemoryForm: React.FC = () => {
  const { control } = useFormContext()
  const [searchTerm, setSearchTerm] = useState('')

  const {
    data: options,
    isLoading: isLoadingOptions
  } = ClimaCheckQuery.getClimaCheckSettingsOptions() as { data: MemoryFieldsData | undefined, isLoading: boolean }

  const { fields: memoryFields, append, remove } = useFieldArray({
    control,
    name: 'memoryFields'
  })

  function renderLoader() {
    return <Dimmer inverted>
      <Loader />
    </Dimmer>
  }

  function isFieldSelected(key: string): boolean {
    return memoryFields.some(memoryField => key === memoryField['slug'])
  }

  function handleFieldToggle(field: MemoryField, checked: boolean) {
    if (checked) {
      append({ slug: field.key })
    } else {
      const index = memoryFields.findIndex(memoryField => field.key === memoryField['slug'])
      if (index >= 0) {
        remove(index)
      }
    }
  }

  function handleSectionToggle(sectionFields: MemoryField[], checked: boolean) {
    if (checked) {
      sectionFields.forEach(field => {
        if (!isFieldSelected(field.key)) {
          append({ slug: field.key })
        }
      })
    } else {
      const indicesToRemove = sectionFields
        .map(field => memoryFields.findIndex(memoryField => field.key === memoryField['slug']))
        .filter(index => index >= 0)
        .sort((a, b) => b - a)

      indicesToRemove.forEach(index => remove(index))
    }
  }

  const filteredSections = useMemo(() => {
    if (!options?.memoryFields) return []

    const sections = Object.entries(options.memoryFields)
    const lowerSearch = searchTerm.toLowerCase().trim()

    if (!lowerSearch) return sections

    return sections
      .map(([sectionName, sectionFields]) => {
        const sectionMatches = sectionName.toLowerCase().includes(lowerSearch)
        const filteredFields = sectionFields.filter(field =>
          field.label.toLowerCase().includes(lowerSearch) ||
          field.key.toLowerCase().includes(lowerSearch)
        )

        if (sectionMatches) {
          return [sectionName, sectionFields] as [string, MemoryField[]]
        }

        if (filteredFields.length > 0) {
          return [sectionName, filteredFields] as [string, MemoryField[]]
        }

        return null
      })
      .filter((section): section is [string, MemoryField[]] => section !== null)
  }, [options?.memoryFields, searchTerm])

  function renderMemoryFields() {
    if (!options?.memoryFields) return null

    if (filteredSections.length === 0 && searchTerm) {
      return (
        <div style={{ textAlign: 'center', padding: 40 }}>
          {'Keine Ergebnisse gefunden'}
        </div>
      )
    }

    return filteredSections.map(([sectionName, sectionFields]) => {
      const allSelected = sectionFields.every(field => isFieldSelected(field.key))

      return (
        <div key={sectionName} style={{
          marginBottom: 32,
          border: '1px solid #e0e0e0',
          borderRadius: 8,
          overflow: 'hidden'
        }}>
          <div style={{
            fontWeight: 'bold',
            padding: '12px 16px',
            backgroundColor: '#f8f9fa',
            borderBottom: '1px solid #e0e0e0'
          }}>
            <Checkbox
              checked={allSelected}
              slider
              onChange={(_, data) => handleSectionToggle(sectionFields, !!data.checked)}
              style={{ marginRight: 4 }}
              label={sectionName}
            />
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '12px 16px',
            padding: 16
          }}>
            {sectionFields.map(field => (
              <Checkbox
                key={field.key}
                checked={isFieldSelected(field.key)}
                onChange={(_, data) => handleFieldToggle(field, !!data.checked)}
                label={field.label}
              />
            ))}
          </div>
        </div>
      )
    })
  }

  return (
    <div style={{
      padding: 20,
      position: 'relative'
    }}>
      <div style={{ marginBottom: 20 }}>
        <Input
          icon='search'
          placeholder='Suchen...'
          value={searchTerm}
          onChange={(_, data) => setSearchTerm(data.value)}
          style={{ width: 300 }}
        />
      </div>
      {isLoadingOptions ? renderLoader() : renderMemoryFields()}
    </div>
  )
}
export default MemoryForm